import { useQuery, useMutation } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Users, Search, Shield, ChevronDown, UserPlus, Pencil, Trash2, Check, KeyRound } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { PageShell } from "@/components/page-shell";

type UserRow = {
  id: string;
  email: string;
  username: string;
  fullName: string;
  organizationId: string | null;
  organizationName: string | null;
  isActive: boolean;
  roles: string[];
  createdAt: string;
  groupIds?: string[];
  department?: string;
};

type RoleRow = { name: string; description: string | null; isSystem?: boolean };
type LiteOption = { id: string; name: string };

export default function OrgUsersPage() {
  const { user, organization } = useAuth();
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [editTarget, setEditTarget] = useState<UserRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserRow | null>(null);

  const { data: users, isLoading } = useQuery<UserRow[]>({ queryKey: ["/api/users"] });
  const { data: roles } = useQuery<RoleRow[]>({ queryKey: ["/api/roles"] });
  const { data: groups } = useQuery<LiteOption[]>({ queryKey: ["/api/groups/list"] });
  const { data: departments } = useQuery<LiteOption[]>({ queryKey: ["/api/departments/list"] });

  // Roles a tenant admin may actually assign.
  //   • Built-in system roles — the backend enforces the same list server-side.
  //   • Custom org roles (isSystem=false) — scoped to the caller's own org and
  //     returned by GET /api/roles already filtered to the caller's org, so
  //     there's no cross-tenant risk in including all of them here.
  // The platform "Admin" role is always excluded for non-platform-admins.
  const ORG_ASSIGNABLE_BUILTIN = ["OrgAdmin", "Member", "Supervisor", "LiveAgent", "Auditor"];
  const isPlatformAdmin = (user as any)?.role === "super_admin";
  const assignableRoles = useMemo(
    () => (roles || []).filter(r =>
      isPlatformAdmin ||
      ORG_ASSIGNABLE_BUILTIN.includes(r.name) ||
      !r.isSystem   // custom roles owned by this org
    ),
    [roles, isPlatformAdmin],
  );

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return (users || []).filter(u =>
      !s || u.email.toLowerCase().includes(s) || u.fullName?.toLowerCase().includes(s) || u.username.toLowerCase().includes(s)
    );
  }, [users, search]);

  const updateUser = useMutation({
    mutationFn: async (vars: {
      id: string;
      patch: Partial<{
        fullName: string; isActive: boolean; roles: string[];
        email: string; username: string; groupIds: string[];
        departmentId: string;
      }>;
    }) => apiRequest("PATCH", `/api/users/${vars.id}`, vars.patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      toast({ title: "User updated" });
    },
    onError: (e: any) => toast({ title: "Update failed", description: e?.message, variant: "destructive" }),
  });

  const deleteUser = useMutation({
    mutationFn: async (id: string) => apiRequest("DELETE", `/api/users/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      toast({ title: "User deactivated", description: "Their access is revoked and active sessions are signed out." });
    },
    onError: (e: any) => toast({ title: "Deactivation failed", description: e?.message, variant: "destructive" }),
  });

  const createUser = useMutation({
    mutationFn: async (body: {
      email: string; username?: string; fullName?: string;
      password?: string; roles: string[]; groupId?: string;
      departmentId?: string; sendWelcomeEmail: boolean;
    }) => apiRequest("POST", "/api/users", body),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["/api/users"] });
      if (data?.emailWarning) {
        toast({ title: "User created", description: data.emailWarning, variant: "default" });
      } else if (data?.emailSent === true) {
        toast({ title: "User created", description: "Welcome email sent." });
      } else {
        toast({ title: "User created" });
      }
    },
    onError: (e: any) => toast({ title: "Create failed", description: e?.message, variant: "destructive" }),
  });

  return (
    <PageShell
      title={
        <span className="flex items-center gap-2">
          <Users className="w-5 h-5 text-primary" /> User Management
        </span>
      }
      subtitle={`Manage users in ${organization?.name || "your organization"}.`}
      titleTestId="text-org-users-title"
      className="pb-28"
    >
      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <CardTitle className="text-lg">Users ({filtered.length})</CardTitle>
            <CardDescription>Activate, deactivate, and assign one or more roles.</CardDescription>
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end w-full sm:w-auto">
            <div className="relative w-full sm:w-64">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search users…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8 w-full"
                data-testid="input-search-users"
              />
            </div>
            <CreateUserDialog
              allRoles={assignableRoles}
              groups={groups || []}
              departments={departments || []}
              isPending={createUser.isPending}
              onCreate={(body) => createUser.mutateAsync(body)}
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-14 w-full" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground">
              <Users className="w-8 h-8 mx-auto mb-2 opacity-40" />
              No users found.
            </div>
          ) : (
            <>
            {/* 320px reflow: below md each user becomes a stacked card so no
                page-level horizontal scrolling is needed. */}
            <ul className="md:hidden space-y-3" data-testid="list-users-mobile">
              {filtered.map(u => (
                <li key={u.id} className="rounded-md border border-border p-3 space-y-2" data-testid={`row-user-${u.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium break-words" data-testid={`text-user-name-${u.id}`}>{u.fullName || u.username}</div>
                      <div className="text-xs text-muted-foreground break-all" data-testid={`text-user-email-${u.id}`}>{u.email}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 w-7 p-0"
                        onClick={() => setEditTarget(u)}
                        data-testid={`button-edit-user-${u.id}`}
                        aria-label={`Edit ${u.fullName || u.email}`}
                      >
                        <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                      </Button>
                      <Button
                        size="sm" variant="ghost"
                        className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                        disabled={u.id === user?.id}
                        onClick={() => setDeleteTarget(u)}
                        data-testid={`button-delete-user-${u.id}`}
                        aria-label={`Deactivate ${u.fullName || u.email}`}
                        title="Deactivate user"
                      >
                        <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                      </Button>
                    </div>
                  </div>
                  {u.organizationName && (
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Tenant</span>
                      <Badge variant="outline" className="font-normal max-w-full whitespace-normal break-words" data-testid={`text-user-tenant-${u.id}`}>
                        {u.organizationName}
                      </Badge>
                    </div>
                  )}
                  <div className="space-y-1">
                    <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Roles</span>
                    <RolesPicker
                      userRoles={u.roles}
                      allRoles={roles || []}
                      disabled={u.id === user?.id}
                      testId={`roles-${u.id}`}
                      onChange={(next) => updateUser.mutate({ id: u.id, patch: { roles: next } })}
                    />
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={u.isActive}
                        disabled={u.id === user?.id || updateUser.isPending}
                        onCheckedChange={(v) => updateUser.mutate({ id: u.id, patch: { isActive: v } })}
                        aria-label={`${u.isActive ? "Deactivate" : "Activate"} ${u.fullName || u.email || "user"}`}
                        data-testid={`switch-active-${u.id}`}
                      />
                      <Badge variant={u.isActive ? "default" : "secondary"} className="text-[10px]">
                        {u.isActive ? "Active" : "Disabled"}
                      </Badge>
                    </div>
                    <span className="text-xs text-muted-foreground">Joined {new Date(u.createdAt).toLocaleDateString()}</span>
                  </div>
                </li>
              ))}
            </ul>
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm min-w-[860px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground">
                    <th scope="col" className="px-4 py-2">User</th>
                    <th scope="col" className="px-2 py-2">Tenant</th>
                    <th scope="col" className="px-2 py-2">Roles</th>
                    <th scope="col" className="px-2 py-2">Active</th>
                    <th scope="col" className="px-2 py-2">Joined</th>
                    <th scope="col" className="px-2 py-2"><span className="sr-only">Edit</span></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map(u => (
                    <tr key={u.id} className="border-t border-border align-top" data-testid={`row-user-${u.id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium" data-testid={`text-user-name-${u.id}`}>{u.fullName || u.username}</div>
                        <div className="text-xs text-muted-foreground" data-testid={`text-user-email-${u.id}`}>{u.email}</div>
                      </td>
                      <td className="px-2 py-3">
                        {u.organizationName ? (
                          <Badge variant="outline" className="font-normal max-w-[180px] truncate" data-testid={`text-user-tenant-${u.id}`}>
                            {u.organizationName}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground" data-testid={`text-user-tenant-${u.id}`}>—</span>
                        )}
                      </td>
                      <td className="px-2 py-3">
                        <RolesPicker
                          userRoles={u.roles}
                          allRoles={roles || []}
                          disabled={u.id === user?.id}
                          testId={`roles-${u.id}`}
                          onChange={(next) => updateUser.mutate({ id: u.id, patch: { roles: next } })}
                        />
                      </td>
                      <td className="px-2 py-3">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={u.isActive}
                            disabled={u.id === user?.id || updateUser.isPending}
                            onCheckedChange={(v) => updateUser.mutate({ id: u.id, patch: { isActive: v } })}
                            aria-label={`${u.isActive ? "Deactivate" : "Activate"} ${u.fullName || u.email || "user"}`}
                            data-testid={`switch-active-${u.id}`}
                          />
                          <Badge variant={u.isActive ? "default" : "secondary"} className="text-[10px]">
                            {u.isActive ? "Active" : "Disabled"}
                          </Badge>
                        </div>
                      </td>
                      <td className="px-2 py-3 text-xs text-muted-foreground">
                        {new Date(u.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-2 py-3">
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm" variant="ghost"
                            className="h-7 w-7 p-0"
                            onClick={() => setEditTarget(u)}
                            data-testid={`button-edit-user-${u.id}`}
                            aria-label={`Edit ${u.fullName || u.email}`}
                          >
                            <Pencil className="w-3.5 h-3.5" aria-hidden="true" />
                          </Button>
                          <Button
                            size="sm" variant="ghost"
                            className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                            disabled={u.id === user?.id}
                            onClick={() => setDeleteTarget(u)}
                            data-testid={`button-delete-user-${u.id}`}
                            aria-label={`Deactivate ${u.fullName || u.email}`}
                            title="Deactivate user"
                          >
                            <Trash2 className="w-3.5 h-3.5" aria-hidden="true" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Shield className="w-4 h-4" /> Need to change permissions?
          </CardTitle>
          <CardDescription>
            Roles and the permissions they grant are managed on the{" "}
            <a href="/admin/org/roles" className="text-primary underline hover:no-underline">Roles</a> page.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Edit user dialog — controlled by editTarget state */}
      {editTarget && (
        <EditUserDialog
          target={editTarget}
          allRoles={assignableRoles}
          groups={groups || []}
          departments={departments || []}
          isPending={updateUser.isPending}
          onSave={async (patch) => {
            await updateUser.mutateAsync({ id: editTarget.id, patch });
            setEditTarget(null);
          }}
          onClose={() => setEditTarget(null)}
        />
      )}

      {/* Deactivation confirmation — triggered from the table row Deactivate icon */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate user?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate{" "}
              <strong>{deleteTarget?.fullName || deleteTarget?.email}</strong>. They'll be signed out
              immediately and their active sessions revoked, so they lose access right away. Their
              account record and history are retained.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteUser.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={deleteUser.isPending}
              onClick={async () => {
                if (deleteTarget) {
                  await deleteUser.mutateAsync(deleteTarget.id);
                  setDeleteTarget(null);
                }
              }}
              data-testid="button-confirm-delete-user"
            >
              {deleteUser.isPending ? "Deactivating…" : "Deactivate user"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageShell>
  );
}

// ── Create user dialog ────────────────────────────────────────────────────────

function CreateUserDialog({ allRoles, groups, departments, isPending, onCreate }: {
  allRoles: RoleRow[];
  groups: LiteOption[];
  departments: LiteOption[];
  isPending: boolean;
  onCreate: (body: {
    email: string; username?: string; fullName?: string;
    password?: string; roles: string[]; groupId?: string;
    departmentId?: string; sendWelcomeEmail: boolean;
  }) => Promise<unknown>;
}) {
  const [open, setOpen] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [roles, setRoles] = useState<string[]>([]);
  const [groupId, setGroupId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(false);

  const reset = () => {
    setFullName(""); setEmail(""); setUsername(""); setPassword(""); setRoles([]);
    setGroupId(""); setDepartmentId(""); setSendWelcomeEmail(false);
  };

  // Password is required only when NOT using welcome-email invite flow.
  const passwordOk = sendWelcomeEmail || password.trim().length > 0;
  const canSubmit = email.trim().length > 0 && passwordOk && !isPending;

  const submit = async () => {
    if (!canSubmit) return;
    await onCreate({
      email: email.trim(),
      username: username.trim() || undefined,
      fullName: fullName.trim() || undefined,
      password: sendWelcomeEmail ? undefined : password,
      roles,
      groupId: groupId || undefined,
      departmentId: departmentId || undefined,
      sendWelcomeEmail,
    });
    reset();
    setOpen(false);
  };

  const toggleRole = (name: string) =>
    setRoles(d => d.includes(name) ? d.filter(r => r !== name) : [...d, name]);

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) {
          // Refetch pickers on every open so freshly created groups /
          // departments show up without a page refresh.
          queryClient.invalidateQueries({ queryKey: ["/api/groups/list"] });
          queryClient.invalidateQueries({ queryKey: ["/api/departments/list"] });
        }
        if (!v) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button className="w-full sm:w-auto shrink-0" data-testid="button-new-user">
          <UserPlus className="w-4 h-4 mr-1.5" /> New User
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md max-h-[90dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create user</DialogTitle>
          <DialogDescription>
            {sendWelcomeEmail
              ? "An invitation email with a set-password link will be sent to the user."
              : "Add a new user to your organization. They can sign in immediately with the password you set."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="cu-fullname">Full name</Label>
            <Input id="cu-fullname" value={fullName} onChange={e => setFullName(e.target.value)}
              placeholder="Jane Doe" data-testid="input-new-fullname" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cu-email">Email <span className="text-destructive">*</span></Label>
            <Input id="cu-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="jane@example.com" data-testid="input-new-email" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cu-username">Username</Label>
            <Input id="cu-username" value={username} onChange={e => setUsername(e.target.value)}
              placeholder="Defaults to email if blank" data-testid="input-new-username" />
          </div>

          {/* Send Welcome Email toggle */}
          <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
            <div className="space-y-0.5">
              <Label htmlFor="cu-welcome-email" className="cursor-pointer">Send Welcome Email</Label>
              <p className="text-[11px] text-muted-foreground leading-tight">
                Email the user a set-password link instead of setting a password now.
              </p>
            </div>
            <Switch
              id="cu-welcome-email"
              checked={sendWelcomeEmail}
              onCheckedChange={setSendWelcomeEmail}
              aria-label="Send welcome email"
              data-testid="switch-send-welcome-email"
            />
          </div>

          {/* Password field — hidden when invite flow is active */}
          {!sendWelcomeEmail && (
            <div className="space-y-1.5">
              <Label htmlFor="cu-password">Temporary password <span className="text-destructive">*</span></Label>
              <Input id="cu-password" type="text" value={password} onChange={e => setPassword(e.target.value)}
                placeholder="At least 8 characters" data-testid="input-new-password" />
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="cu-group">Group</Label>
              <Select value={groupId} onValueChange={setGroupId}>
                <SelectTrigger id="cu-group" data-testid="select-new-group">
                  <SelectValue placeholder={groups.length === 0 ? "No groups" : "Select group (optional)"} />
                </SelectTrigger>
                <SelectContent>
                  {groups.map(g => (
                    <SelectItem key={g.id} value={g.id} data-testid={`option-new-group-${g.id}`}>{g.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cu-department">Department</Label>
              <Select value={departmentId} onValueChange={setDepartmentId}>
                <SelectTrigger id="cu-department" data-testid="select-new-department">
                  <SelectValue placeholder={departments.length === 0 ? "No departments" : "Select dept (optional)"} />
                </SelectTrigger>
                <SelectContent>
                  {departments.map(d => (
                    <SelectItem key={d.id} value={d.id} data-testid={`option-new-department-${d.id}`}>{d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Roles <span className="text-destructive">*</span></Label>
            <div className="space-y-1 max-h-44 overflow-y-auto rounded border border-border p-1.5">
              {allRoles.length === 0 && <div className="text-xs text-muted-foreground p-1.5">No roles available.</div>}
              {allRoles.map(r => (
                <label key={r.name} className="flex items-start gap-2 p-1.5 rounded hover:bg-muted cursor-pointer">
                  <Checkbox checked={roles.includes(r.name)} onCheckedChange={() => toggleRole(r.name)}
                    data-testid={`checkbox-new-role-${r.name}`} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium leading-tight">{r.name}</div>
                    {r.description && <div className="text-[11px] text-muted-foreground leading-tight">{r.description}</div>}
                  </div>
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit} data-testid="button-create-user">
            {isPending ? "Creating…" : sendWelcomeEmail ? "Invite user" : "Create user"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Edit user dialog ──────────────────────────────────────────────────────────

function EditUserDialog({ target, allRoles, groups, departments, isPending, onSave, onClose }: {
  target: UserRow;
  allRoles: RoleRow[];
  groups: LiteOption[];
  departments: LiteOption[];
  isPending: boolean;
  onSave: (patch: {
    fullName?: string; email?: string; username?: string;
    roles?: string[]; groupIds?: string[]; departmentId?: string; isActive?: boolean;
  }) => Promise<void>;
  onClose: () => void;
}) {
  const [fullName, setFullName] = useState(target.fullName || "");
  const [email, setEmail] = useState(target.email || "");
  const [username, setUsername] = useState(target.username || "");
  const [roles, setRoles] = useState<string[]>(target.roles || []);
  const [groupIds, setGroupIds] = useState<string[]>(target.groupIds?.map(String) || []);
  // Pre-populate department: find its ID from the departments list by name
  // "__none__" is the UI sentinel for "no department"; "" is invalid in Radix Select.
  const DEPT_NONE = "__none__";
  const initDeptId = useMemo(
    () => departments.find(d => d.name === target.department)?.id ?? DEPT_NONE,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  const [departmentId, setDepartmentId] = useState(initDeptId);
  const [isActive, setIsActive] = useState(target.isActive);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const { toast } = useToast();

  const resetPasswordMut = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/users/${target.id}/reset-password`);
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).message || "Failed to send password reset.");
      }
      return res.json();
    },
    onSuccess: () => {
      toast({
        title: "Password reset sent",
        description: `A reset link has been emailed to ${target.email}.`,
      });
      setResetConfirmOpen(false);
    },
    onError: (err: Error) => {
      toast({ title: "Reset failed", description: err.message, variant: "destructive" });
      setResetConfirmOpen(false);
    },
  });

  const toggleRole = (name: string) =>
    setRoles(d => d.includes(name) ? d.filter(r => r !== name) : [...d, name]);
  const toggleGroup = (id: string) =>
    setGroupIds(d => d.includes(id) ? d.filter(g => g !== id) : [...d, id]);

  const canSubmit = email.trim().length > 0 && !isPending;

  const submit = async () => {
    if (!canSubmit) return;
    await onSave({
      fullName: fullName.trim() || undefined,
      email: email.trim() !== target.email ? email.trim() : undefined,
      username: username.trim() !== target.username ? username.trim() : undefined,
      roles,
      groupIds,
      // DEPT_NONE selected and user previously had a dept → send Guid.Empty to clear
      departmentId: departmentId === DEPT_NONE && initDeptId !== DEPT_NONE
        ? "00000000-0000-0000-0000-000000000000"
        : departmentId !== DEPT_NONE && departmentId !== initDeptId ? departmentId : undefined,
      isActive,
    });
  };

  const selectedGroupNames = groups.filter(g => groupIds.includes(g.id)).map(g => g.name);

  return (
    <>
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent className="sm:max-w-md max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit user</DialogTitle>
            <DialogDescription>
              Update <strong>{target.fullName || target.email}</strong>'s profile, roles, groups, and status.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label htmlFor="eu-fullname">Full name</Label>
              <Input id="eu-fullname" value={fullName} onChange={e => setFullName(e.target.value)}
                placeholder="Jane Doe" data-testid="input-edit-fullname" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="eu-email">Email <span className="text-destructive">*</span></Label>
              <Input id="eu-email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                data-testid="input-edit-email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="eu-username">Username</Label>
              <Input id="eu-username" value={username} onChange={e => setUsername(e.target.value)}
                placeholder="Defaults to email if blank" data-testid="input-edit-username" />
            </div>

            {/* Active status */}
            <div className="flex items-center justify-between rounded-md border border-border px-3 py-2.5">
              <div className="space-y-0.5">
                <Label htmlFor="eu-active" className="cursor-pointer">Active</Label>
                <p className="text-[11px] text-muted-foreground leading-tight">
                  Deactivating immediately invalidates the user's current sessions.
                </p>
              </div>
              <Switch
                id="eu-active"
                checked={isActive}
                onCheckedChange={setIsActive}
                aria-label={isActive ? "Deactivate user" : "Activate user"}
                data-testid="switch-edit-active"
              />
            </div>

            {/* Roles */}
            <div className="space-y-1.5">
              <Label>Roles</Label>
              <div className="space-y-1 max-h-36 overflow-y-auto rounded border border-border p-1.5">
                {allRoles.length === 0 && <div className="text-xs text-muted-foreground p-1.5">No roles available.</div>}
                {allRoles.map(r => (
                  <label key={r.name} className="flex items-start gap-2 p-1.5 rounded hover:bg-muted cursor-pointer">
                    <Checkbox checked={roles.includes(r.name)} onCheckedChange={() => toggleRole(r.name)}
                      data-testid={`checkbox-edit-role-${r.name}`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium leading-tight">{r.name}</div>
                      {r.description && <div className="text-[11px] text-muted-foreground leading-tight">{r.description}</div>}
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Groups — multi-select popover dropdown */}
              <div className="space-y-1.5">
                <Label>Groups</Label>
                <Popover open={groupsOpen} onOpenChange={setGroupsOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={groupsOpen}
                      className="w-full justify-between font-normal h-auto min-h-9 py-1 px-3"
                      data-testid="button-edit-groups"
                    >
                      <span className="text-left flex flex-wrap gap-1 flex-1 min-w-0">
                        {selectedGroupNames.length === 0 ? (
                          <span className="text-muted-foreground text-sm">
                            {groups.length === 0 ? "No groups" : "None selected"}
                          </span>
                        ) : (
                          selectedGroupNames.map(n => (
                            <Badge key={n} variant="secondary" className="text-[10px] font-normal">{n}</Badge>
                          ))
                        )}
                      </span>
                      <ChevronDown className="ml-1 shrink-0 w-4 h-4 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-1" align="start">
                    {groups.length === 0 ? (
                      <p className="text-xs text-muted-foreground p-2">No groups in this organization.</p>
                    ) : (
                      groups.map(g => (
                        <button
                          key={g.id}
                          type="button"
                          className="flex items-center gap-2 w-full px-2 py-1.5 rounded text-sm hover:bg-muted text-left"
                          onClick={() => toggleGroup(g.id)}
                          data-testid={`option-edit-group-${g.id}`}
                        >
                          <Check className={`w-4 h-4 shrink-0 ${groupIds.includes(g.id) ? "opacity-100" : "opacity-0"}`} />
                          {g.name}
                        </button>
                      ))
                    )}
                  </PopoverContent>
                </Popover>
              </div>

              {/* Department dropdown */}
              <div className="space-y-1.5">
                <Label htmlFor="eu-department">Department</Label>
                <Select value={departmentId} onValueChange={setDepartmentId}>
                  <SelectTrigger id="eu-department" data-testid="select-edit-department">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={DEPT_NONE} data-testid="option-edit-department-none">None</SelectItem>
                    {departments.map(d => (
                      <SelectItem key={d.id} value={d.id} data-testid={`option-edit-department-${d.id}`}>
                        {d.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col-reverse sm:flex-row sm:justify-between gap-2 pt-1">
            {/* Reset password — left side, only for active users */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-destructive border-destructive/40 hover:bg-destructive/5 sm:mr-auto"
              onClick={() => setResetConfirmOpen(true)}
              disabled={isPending || resetPasswordMut.isPending || !target.isActive}
              data-testid="button-reset-password"
            >
              <KeyRound className="w-3.5 h-3.5 mr-1.5" aria-hidden="true" />
              Send Password Reset
            </Button>
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={onClose} disabled={isPending}>Cancel</Button>
              <Button onClick={submit} disabled={!canSubmit} data-testid="button-save-edit-user">
                {isPending ? "Saving…" : "Save changes"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirmation before triggering the reset — sibling to Dialog to avoid
          Radix nested-dialog focus conflicts */}
      <AlertDialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send password reset?</AlertDialogTitle>
            <AlertDialogDescription>
              A password reset link will be emailed to{" "}
              <strong>{target.email}</strong>. Their current password stays valid
              until they use the link. You will not see the token.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetPasswordMut.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); resetPasswordMut.mutate(); }}
              disabled={resetPasswordMut.isPending}
              data-testid="button-confirm-reset-password"
            >
              {resetPasswordMut.isPending ? "Sending…" : "Send reset email"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// ── RolesPicker (inline popover for quick role edits in the table) ─────────────

function RolesPicker({ userRoles, allRoles, disabled, onChange, testId }: {
  userRoles: string[]; allRoles: RoleRow[]; disabled?: boolean;
  onChange: (next: string[]) => void; testId: string;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(userRoles);
  // Keep draft in sync when the parent's roles change (after a save).
  useMemoSyncDraft(userRoles, setDraft, open);

  const toggle = (name: string) => {
    setDraft(d => d.includes(name) ? d.filter(r => r !== name) : [...d, name]);
  };
  const apply = () => { onChange(draft); setOpen(false); };
  const cancel = () => { setDraft(userRoles); setOpen(false); };

  return (
    <Popover open={open} onOpenChange={(v) => { if (!v) cancel(); else setOpen(true); }}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className="h-auto min-h-8 py-1 px-2 max-w-[260px] flex-wrap justify-start gap-1"
          data-testid={`button-${testId}`}
        >
          {userRoles.length === 0 ? (
            <span className="text-muted-foreground text-xs">No roles</span>
          ) : (
            userRoles.map(r => (
              <Badge key={r} variant="secondary" className="text-[10px]">{r}</Badge>
            ))
          )}
          <ChevronDown className="w-3 h-3 ml-auto opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 p-2" data-testid={`popover-${testId}`}>
        <div className="text-[11px] uppercase tracking-wider text-muted-foreground px-1 pb-1">Assign roles</div>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {allRoles.length === 0 && <div className="text-xs text-muted-foreground p-2">No roles available.</div>}
          {allRoles.map(r => (
            <label key={r.name} className="flex items-start gap-2 p-1.5 rounded hover:bg-muted cursor-pointer">
              <Checkbox
                checked={draft.includes(r.name)}
                onCheckedChange={() => toggle(r.name)}
                data-testid={`checkbox-${testId}-${r.name}`}
              />
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium leading-tight">{r.name}</div>
                {r.description && <div className="text-[11px] text-muted-foreground leading-tight">{r.description}</div>}
              </div>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-border mt-2">
          <Button size="sm" variant="ghost" onClick={cancel}>Cancel</Button>
          <Button size="sm" onClick={apply} data-testid={`button-apply-${testId}`}>Apply</Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

// Re-sync a draft state back to the source-of-truth whenever the source
// changes AND the popover is closed (so we don't clobber user edits).
function useMemoSyncDraft(source: string[], setDraft: (s: string[]) => void, open: boolean) {
  const key = source.slice().sort().join(",");
  useMemo(() => { if (!open) setDraft(source); /* eslint-disable-next-line */ }, [key, open]);
}
